/* eslint-disable import/no-extraneous-dependencies */
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDroppable } from '@dnd-kit/core';
import { getDevFlag } from '@frameio/shared/dev-flags';
import { theme } from '@frameio/vapor-theme';
import {
  ASPECT_RATIOS_NUMBERS,
  BASE_FOR_SCALE,
  MOBILE_MAX_PREVIEW_HEIGHT,
  MOBILE_WIDTH,
  SAFE_ZONE_PERCENTAGE,
} from '../../constants';
import type { AspectRatio, WatermarkBlockData } from '../../types';
import { WatermarkBlock } from './WatermarkBlock';

type PreviewAreaProps = {
  aspectRatio: string;
  blocks: WatermarkBlockData[];
  containerWidth: number;
  containerHeight: number;
  onScaleChange: (scale: number) => void;
  onSelectBlock: (blockId: string) => void;
  safeZone: boolean;
  selectedBlockId: string;
  backgroundImage: string;
  setPreviewDimensions: ({
    width,
    height,
  }: {
    width: number;
    height: number;
  }) => void;
  isReadOnly?: boolean;
};

type StyledPreviewContainerProps = {
  $ratio: AspectRatio;
};

// Calculate dimensions based on aspect ratio and container size
const calculateDimensions = ({
  aspectRatio,
  containerSize,
}: {
  aspectRatio: string;
  containerSize: { width: number; height: number };
}): { width: number; height: number } => {
  const containerAspectRatio = containerSize.width / containerSize.height;
  const targetAspectRatio = ASPECT_RATIOS_NUMBERS[aspectRatio];
  let width: number;
  let height: number;

  if (containerAspectRatio > targetAspectRatio) {
    height = containerSize.height;
    width = height * targetAspectRatio;
  } else {
    width = containerSize.width;
    height = width / targetAspectRatio;
  }

  return { width: Math.floor(width), height: Math.floor(height) };
};

/**
 * PreviewArea Component
 *
 * This component renders a preview area for the watermark template editor.
 * It handles dynamic sizing, aspect ratio adjustments, and rendering of watermark blocks.
 */
export const PreviewArea = forwardRef<HTMLDivElement, PreviewAreaProps>(
  (
    {
      aspectRatio,
      blocks,
      containerWidth,
      containerHeight,
      onScaleChange,
      onSelectBlock,
      safeZone,
      selectedBlockId,
      backgroundImage,
      setPreviewDimensions,
      isReadOnly = false,
    },
    ref
  ) => {
    const { setNodeRef } = useDroppable({ id: 'preview-area' });
    const isWMIDDebugModeEnabled = getDevFlag('ENABLE_WMID_DEBUG_MODE');
    const [scale, setScale] = useState(1);
    const [containerSize, setContainerSize] = useState({
      width: containerWidth,
      height: containerHeight,
    });
    const containerRef = useRef<HTMLDivElement>(null);

    const [isMobile, setIsMobile] = useState(false);

    // Update container size on mount and resize
    useEffect(() => {
      const updateSize = () => {
        if (containerRef.current) {
          const { offsetWidth, offsetHeight } = containerRef.current;
          setContainerSize({ width: offsetWidth, height: offsetHeight });
          const w = window.innerWidth;
          setIsMobile(w < MOBILE_WIDTH);
        }
      };

      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Calculate and update scale
    const calculateScale = useCallback(() => {
      let newScale = containerSize.width / BASE_FOR_SCALE;
      if (isMobile) {
        const targetAspectRatio = ASPECT_RATIOS_NUMBERS[aspectRatio];
        const h = containerSize.height;
        const w = h * targetAspectRatio;
        newScale = w / BASE_FOR_SCALE;
      }
      setScale(newScale);
      onScaleChange(newScale);
    }, [
      containerSize.width,
      onScaleChange,
      isMobile,
      aspectRatio,
      containerSize.height,
    ]);

    useEffect(() => {
      calculateScale();
    }, [calculateScale]);

    // Combine refs
    const setRefs = (node: HTMLDivElement) => {
      setNodeRef(node);
      if (typeof ref === 'function') ref(node);
      // eslint-disable-next-line no-param-reassign
      else if (ref) ref.current = node;
    };

    const { width, height } = calculateDimensions({
      aspectRatio,
      containerSize,
    });

    // Update preview dimensions when width or height changes
    useEffect(() => {
      setPreviewDimensions({ width, height });
    }, [width, height, setPreviewDimensions]);

    return (
      <SizingContainer ref={containerRef}>
        <RatioContainer height={`${height}px`} width={`${width}px`}>
          <PreviewContainer
            ref={setRefs}
            $ratio={aspectRatio as AspectRatio}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {blocks.map((block) => (
              <WatermarkBlock
                key={block.id}
                block={block}
                containerHeight={height}
                containerWidth={width}
                isReadOnly={isReadOnly}
                isSelected={block.id === selectedBlockId}
                onSelect={onSelectBlock}
                scale={scale}
              />
            ))}
            {safeZone && <SafeZoneOverlay />}
            {isWMIDDebugModeEnabled && (
              <DebugGrid>
                {Array.from({ length: 9 }).map((_, i) => (
                  <GridCell key={i} />
                ))}
              </DebugGrid>
            )}
          </PreviewContainer>
        </RatioContainer>
      </SizingContainer>
    );
  }
);

// Preview Area
const DebugGrid = styled.div`
  display: grid;
  grid-template: repeat(3, 1fr) / repeat(3, 1fr);
  inset: 0;
  pointer-events: none;
  position: absolute;
`;

const GridCell = styled.div`
  border: 1px dashed ${theme.colors.icon.secondaryStark};
`;

const SizingContainer = styled.div`
  align-items: center;
  background-color: ${theme.colors.background.base};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
  @media (max-width: ${MOBILE_WIDTH}px) {
    height: ${MOBILE_MAX_PREVIEW_HEIGHT};
  }
`;

const RatioContainer = styled.div<{ width: string; height: string }>`
  align-items: center;
  background-color: ${theme.colors.background.base};
  display: flex;
  height: ${(props) => props.height};
  justify-content: center;
  overflow: hidden;
  width: ${(props) => props.width};
`;

const PreviewContainer = styled.div<StyledPreviewContainerProps>`
  aspect-ratio: ${(props) => props.$ratio.replace(':', '/')};
  background-color: ${theme.colors.background.base};
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const SafeZoneOverlay = styled.div`
  border: 2px dashed rgba(255, 255, 255, 0.5);
  bottom: ${SAFE_ZONE_PERCENTAGE}%;
  left: ${SAFE_ZONE_PERCENTAGE}%;
  pointer-events: none;
  position: absolute;
  right: ${SAFE_ZONE_PERCENTAGE}%;
  top: ${SAFE_ZONE_PERCENTAGE}%;
`;
